import {
  type ColumnDef,
  type PaginationState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { type SyntheticEvent, useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import Pagination from "./Pagination";

export type CustomColumnDef<TData, TValue = unknown> = ColumnDef<
  TData,
  TValue
> & {
  canSort?: boolean;
  cellClassName?: string | ((row: TData) => string | undefined);
};

type DataTableProps<TData extends { id: string | number }, TValue> = {
  columns: CustomColumnDef<TData, TValue>[];
  data: TData[];
  isFetching?: boolean;
  noResultsMessage: string;
  onRowActivate?: (row: TData) => void;
  pageSize?: number;
};

const INTERACTIVE_SELECTOR =
  "a, button, input, select, textarea, [role='button'], [role='link'], [tabindex]:not([tabindex='-1'])";

function eventStartedInNestedInteractiveElement(
  event: Pick<SyntheticEvent, "currentTarget" | "target">,
) {
  if (
    !(event.currentTarget instanceof HTMLElement) ||
    !(event.target instanceof HTMLElement)
  ) {
    return false;
  }

  const interactiveElement = event.target.closest(INTERACTIVE_SELECTOR);

  return Boolean(
    interactiveElement &&
      interactiveElement !== event.currentTarget &&
      event.currentTarget.contains(interactiveElement),
  );
}

export default function DataTable<
  TData extends { id: string | number },
  TValue,
>({
  columns,
  data,
  isFetching = false,
  noResultsMessage,
  onRowActivate,
  pageSize = 10,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });

  useEffect(() => {
    setPagination((current) => {
      const pageCount = Math.max(Math.ceil(data.length / pageSize), 1);
      const pageIndex = Math.min(current.pageIndex, pageCount - 1);

      if (current.pageIndex === pageIndex && current.pageSize === pageSize) {
        return current;
      }

      return { pageIndex, pageSize };
    });
  }, [data.length, pageSize]);

  const table = useReactTable({
    data,
    columns,
    autoResetPageIndex: false,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    state: { pagination, sorting },
  });

  const pageCount = Math.max(table.getPageCount(), 1);
  const currentPage = table.getState().pagination.pageIndex + 1;

  return (
    <div className="overflow-hidden bg-card">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="bg-muted-background">
              {headerGroup.headers.map((header) => {
                const columnDef = header.column.columnDef as CustomColumnDef<
                  TData,
                  TValue
                >;
                const sortState = header.column.getIsSorted();

                return (
                  <TableHead
                    key={header.id}
                    className="h-12 border-r border-border px-5 text-xs font-semibold last:border-r-0"
                  >
                    {header.isPlaceholder ? null : columnDef.canSort ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 px-0 text-xs font-semibold hover:bg-transparent"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                        {sortState === "asc" ? (
                          <ArrowUp className="ml-1.5 h-3.5 w-3.5" />
                        ) : sortState === "desc" ? (
                          <ArrowDown className="ml-1.5 h-3.5 w-3.5" />
                        ) : (
                          <ChevronsUpDown className="ml-1.5 h-3.5 w-3.5" />
                        )}
                      </Button>
                    ) : (
                      flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className={cn(isFetching && "opacity-60")}>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className={cn(
                  onRowActivate && "cursor-pointer hover:bg-muted/50",
                )}
                onClick={(event) => {
                  if (eventStartedInNestedInteractiveElement(event)) return;

                  onRowActivate?.(row.original);
                }}
              >
                {row.getVisibleCells().map((cell) => {
                  const columnDef = cell.column.columnDef as CustomColumnDef<
                    TData,
                    TValue
                  >;
                  const cellClassName =
                    typeof columnDef.cellClassName === "function"
                      ? columnDef.cellClassName(row.original)
                      : columnDef.cellClassName;

                  return (
                    <TableCell
                      key={cell.id}
                      className={cn(
                        "border-r border-border px-5 py-4 last:border-r-0",
                        cellClassName,
                      )}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="h-24 text-center text-sm text-muted-foreground"
              >
                {noResultsMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Pagination
        page={currentPage}
        pageCount={pageCount}
        onPageChange={(page) => table.setPageIndex(page - 1)}
      />
    </div>
  );
}
